<script>
  export let rec;
  export let isLast = false;

  // Mobile detection
  let isMobile = false;

  if (typeof window !== "undefined") {
    isMobile = window.innerWidth < 768;
    window.addEventListener("resize", () => {
      isMobile = window.innerWidth < 768;
    });
  }
</script>

<div
  class="bg-white/30 dark:bg-gray-800/20 backdrop-blur-xl border border-white/30 dark:border-gray-700/30
  rounded-3xl p-4 md:p-8 flex-shrink-0 min-w-[260px] sm:min-w-[300px] max-w-[800px] w-fit
  {isLast ? 'mr-4 md:mr-10 lg:mr-16' : 'mr-4 md:mr-10'}
  my-6 md:my-10 h-[400px] md:h-[500px]
  transform transition-all duration-500 ease-out flex flex-col
  hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)]
  hover:border-blue-100/50 dark:hover:border-blue-500/30
  backdrop-saturate-[1.8]
  group"
>
  <div
    class="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-6 flex-shrink-0"
  >
    <div class="flex items-center gap-3 md:gap-5">
      <div class="relative">
        <div
          class="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-[22px] blur opacity-20 group-hover:opacity-30 transition duration-500"
        ></div>
        <img
          src={rec.imageUrl}
          alt={rec.name}
          loading="lazy"
          class="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border-2 border-white/50 dark:border-gray-700/50 shadow-lg"
        />
        <div class="absolute -bottom-2 -right-2 md:-bottom-3 md:-right-3">
          <div class="relative">
            <div
              class="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-500"
            ></div>
            <img
              src={rec.companyImageUrl}
              alt="Company Logo"
              loading="lazy"
              class="relative h-6 md:h-8 max-w-[80px] md:max-w-[100px] rounded-lg shadow-lg border-2 border-white/50 dark:border-gray-800/50 bg-white dark:bg-gray-800 p-1"
            />
          </div>
        </div>
      </div>

      <div>
        <h3
          class="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-colors duration-500"
        >
          {rec.name}
        </h3>
        <p
          class="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-500"
        >
          {rec.position}
        </p>
        <p
          class="text-2xs md:text-xs text-gray-500 dark:text-gray-500 mt-0.5 md:mt-1"
        >
          {rec.relationship} · {rec.date}
        </p>
      </div>
    </div>

    <!-- Desktop verified badge -->
    <div class="hidden md:flex items-center gap-2">
      <div
        class="relative px-4 py-1.5 rounded-full
        bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10
        dark:from-blue-500/20 dark:via-purple-500/20 dark:to-pink-500/20
        border border-blue-300/30 dark:border-blue-500/30
        group-hover:border-blue-400/40 dark:group-hover:border-blue-400/40
        transition-all duration-500"
      >
        <div
          class="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-500"
        ></div>
        <div class="flex items-center gap-2">
          <svg
            class="w-3.5 h-3.5 text-blue-500"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M19.965 8.521C19.988 8.347 20 8.173 20 8c0-2.379-2.143-4.288-4.521-3.965C14.786 2.802 13.466 2 12 2s-2.786.802-3.479 2.035C6.138 3.712 4 5.621 4 8c0 .173.012.347.035.521C2.802 9.215 2 10.535 2 12s.802 2.785 2.035 3.479A3.976 3.976 0 0 0 4 16c0 2.379 2.138 4.283 4.521 3.965C9.214 21.198 10.534 22 12 22s2.786-.802 3.479-2.035C17.857 20.283 20 18.379 20 16c0-.173-.012-.347-.035-.521C21.198 14.785 22 13.465 22 12s-.802-2.785-2.035-3.479zm-9.01 7.895-3.667-3.714 1.424-1.404 2.243 2.272 5.103-5.17 1.424 1.404-6.527 6.612z"
              fill-rule="evenodd"
              clip-rule="evenodd"
            />
          </svg>
          <span class="text-sm font-medium text-blue-600 dark:text-blue-400">
            Verified
          </span>
        </div>
      </div>
    </div>

    <!-- Mobile verified badge (smaller) -->
    <div class="flex md:hidden items-center gap-1 mt-1">
      <svg
        class="w-3 h-3 text-blue-500"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          d="M19.965 8.521C19.988 8.347 20 8.173 20 8c0-2.379-2.143-4.288-4.521-3.965C14.786 2.802 13.466 2 12 2s-2.786.802-3.479 2.035C6.138 3.712 4 5.621 4 8c0 .173.012.347.035.521C2.802 9.215 2 10.535 2 12s.802 2.785 2.035 3.479A3.976 3.976 0 0 0 4 16c0 2.379 2.138 4.283 4.521 3.965C9.214 21.198 10.534 22 12 22s2.786-.802 3.479-2.035C17.857 20.283 20 18.379 20 16c0-.173-.012-.347-.035-.521C21.198 14.785 22 13.465 22 12s-.802-2.785-2.035-3.479zm-9.01 7.895-3.667-3.714 1.424-1.404 2.243 2.272 5.103-5.17 1.424 1.404-6.527 6.612z"
          fill-rule="evenodd"
          clip-rule="evenodd"
        />
      </svg>
      <span class="text-2xs font-medium text-blue-600 dark:text-blue-400">
        Verified
      </span>
    </div>
  </div>

  <div class="mt-3 md:mt-6 flex-1 overflow-hidden flex flex-col">
    <div class="relative h-full overflow-y-auto custom-scrollbar">
      <!-- Quote Icon -->
      <div
        class="absolute left-0 top-0 text-blue-500/10 dark:text-blue-400/10 transition-colors duration-500 group-hover:text-blue-500/20 dark:group-hover:text-blue-400/20"
      >
        <svg
          class="w-8 h-8 md:w-10 md:h-10"
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"
          />
        </svg>
      </div>

      <!-- Recommendation Text -->
      <div class="relative pl-10 md:pl-12 pt-6 md:pt-8">
        <p
          class="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed space-y-3 md:space-y-4"
        >
          {#each rec.recommendation.split("\n") as paragraph}
            {#if paragraph.includes(":")}
              {@html paragraph
                .split(":")
                .map((part, i) =>
                  i === 0
                    ? `<strong class="font-semibold">${part}:</strong>`
                    : part
                )
                .join("")}
            {:else}
              {@html paragraph}
            {/if}
            <br />
          {/each}
        </p>
      </div>
    </div>
  </div>
</div>

<style>
  /* Modern scrollbar styling */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
    border-radius: 100vh;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, #2563eb, #7c3aed);
  }

  /* For Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #3b82f6 transparent;
  }

  /* Extra small text for mobile */
  .text-2xs {
    font-size: 0.625rem;
    line-height: 0.875rem;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .custom-scrollbar::-webkit-scrollbar {
      width: 4px;
    }
  }
</style>
